import { useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useTheme } from '@/lib/ThemeContext';
import { perlin3 } from '@/lib/perlinNoise';

const BRAND = new THREE.Color('#3346D3');
const TINT = new THREE.Color('#7B8BF0');

function WireframeBlob({ isDark }) {
  const meshRef = useRef(null);
  const { viewport } = useThree();
  /* Sit right of the hero text on wide screens, centred on mobile */
  const wide = viewport.aspect >= 1.2;
  const x = wide ? viewport.width * 0.3 : -0.28;

  const geometry = useMemo(() => {
    const geo = new THREE.SphereGeometry(1.8, 44, 32);
    const pos = geo.attributes.position;
    const colors = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      const t = THREE.MathUtils.clamp((pos.getY(i) + 1.8) / 3.6, 0, 1);
      const c = BRAND.clone().lerp(TINT, t);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
  }, []);

  const basePositions = useMemo(() => geometry.attributes.position.array.slice(), [geometry]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const pos = geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const ix = i * 3;
      const bx = basePositions[ix], by = basePositions[ix + 1], bz = basePositions[ix + 2];
      const len = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
      const nx = bx / len, ny = by / len, nz = bz / len;
      const n = perlin3(nx * 1.4 + t * 0.12, ny * 1.4 + t * 0.1, nz * 1.4 + t * 0.15);
      const scale = 1 + n * 0.16;
      pos.setXYZ(i, bx * scale, by * scale, bz * scale);
    }
    pos.needsUpdate = true;

    if (meshRef.current) {
      meshRef.current.rotation.y = t * 0.07;
      meshRef.current.rotation.x = Math.sin(t * 0.09) * 0.18;
    }
  });

  return (
    <mesh ref={meshRef} geometry={geometry} position={[x, -0.2, -1.2]} scale={wide ? 0.85 : 1}>
      <meshBasicMaterial
        vertexColors
        wireframe
        transparent
        opacity={isDark ? 0.3 : 0.22}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
}

export default function HeroCanvas() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" data-testid="hero-canvas">
      <Canvas
        camera={{ position: [0, 0, 5], fov: 42 }}
        dpr={[1, 1.5]}
        gl={{ alpha: true, antialias: true }}
      >
        <WireframeBlob isDark={isDark} />
      </Canvas>
    </div>
  );
}
